/**
 * Samples — Sample CRUD list + sidebar editor
 *
 * URL-driven sidebar: /admin/samples/:id opens the editor drawer.
 * Follows OnMove-Admin ListTemplate pattern:
 *  - Actions column with icon buttons
 *  - Column visibility via header icon
 *  - Full-width search below header
 */

import { useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Chip, IconButton, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import ListPageTemplate from "templates/ListPageTemplate";
import SampleEditor from "./components/SampleEditor";
import KDBox from "components/KDBox";
import KDButton from "components/KDButton";
import useSamples from "hooks/useSamples";
import { useT } from "context/I18nContext";
import { useAbility, ACTIONS, SUBJECTS } from "context/AbilityContext";

const statusColors = {
  published: "success",
  draft: "warning",
  archived: "default",
};

const priorityColors = {
  high: "error",
  medium: "warning",
  low: "info",
};

function Samples() {
  const t = useT();
  const ability = useAbility();
  const navigate = useNavigate();
  const { id: detailId } = useParams();
  const { samples, loading, error, fetchSamples } = useSamples({ autoFetch: true });

  const canCreate = ability.can(ACTIONS.CREATE, SUBJECTS.SAMPLE);
  const canEdit = ability.can(ACTIONS.UPDATE, SUBJECTS.SAMPLE);
  const isDrawerOpen = Boolean(detailId);
  const isNew = detailId === "new";

  const columns = useMemo(
    () => [
      {
        field: "title",
        header: t("samples.title_label"),
        width: "22%",
      },
      {
        field: "description",
        header: t("samples.description_label"),
        width: "28%",
        render: (val) => (val?.length > 60 ? `${val.slice(0, 60)}...` : val),
      },
      {
        field: "category",
        header: t("samples.category"),
        width: "13%",
      },
      {
        field: "status",
        header: t("samples.status"),
        width: "10%",
        render: (val) => (
          <Chip
            label={t(`samples.status_${val}`) || val}
            size="small"
            color={statusColors[val] || "default"}
          />
        ),
      },
      {
        field: "priority",
        header: t("samples.priority"),
        width: "10%",
        render: (val) => (
          <Chip
            label={t(`samples.priority_${val}`) || val}
            size="small"
            color={priorityColors[val] || "default"}
            variant="outlined"
          />
        ),
      },
      {
        field: "actions",
        header: t("common.actions"),
        width: "12%",
        align: "right",
        sortable: false,
        render: (_, row) => (
          <KDBox display="flex" justifyContent="flex-end" gap={0.5}>
            {canEdit && (
              <Tooltip title={t("common.edit")}>
                <IconButton
                  size="small"
                  color="info"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/admin/samples/${row.id}`);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </KDBox>
        ),
      },
    ],
    [t, canEdit, navigate]
  );

  const handleRowClick = useCallback(
    (row) => navigate(`/admin/samples/${row.id}`),
    [navigate]
  );

  const handleCloseDrawer = useCallback(
    () => navigate("/admin/samples", { replace: true }),
    [navigate]
  );

  const handleSaved = useCallback(() => {
    fetchSamples();
  }, [fetchSamples]);

  return (
    <>
      <ListPageTemplate
        title={t("nav.samples")}
        columns={columns}
        rows={samples}
        loading={loading}
        error={error}
        emptyMessage={t("samples.empty")}
        onRowClick={handleRowClick}
        searchable
        searchPlaceholder={t("samples.search")}
        defaultSortField="title"
        headerColor="primary"
        defaultVisibleFields={["title", "description", "category", "status", "priority", "actions"]}
        extraToolbarActions={
          canCreate ? (
            <KDButton
              variant="contained"
              size="small"
              onClick={() => navigate("/admin/samples/new")}
              sx={{
                color: "#fff",
                backgroundColor: "rgba(255,255,255,0.15)",
                borderRadius: "10px",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.25)" },
              }}
            >
              <AddIcon sx={{ mr: 0.5 }} /> {t("samples.create")}
            </KDButton>
          ) : null
        }
      />

      {isDrawerOpen && (
        <SampleEditor
          sampleId={isNew ? null : detailId}
          isNew={isNew}
          onClose={handleCloseDrawer}
          onSaved={handleSaved}
          onDeleted={handleSaved}
        />
      )}
    </>
  );
}

export default Samples;
